/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaSiblingReorder.ts
 * FILE_ID: JOBHUB-QA-SIBLING-REORDER-001
 * PURPOSE: Move one location or checklist one step among its visible siblings.
 */
import { checklistGroups, normalizeList } from '../../qa/qaApiHelpers'
import type { QaArea, QaAreaExecution, QaFloor } from '../../qa/qaApi'
import { levelsOf } from './qaTabSelectors'
import type { JobQaTabController } from './useJobQaTabController'
// Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaTreeMoves.ts (reorder, swappedOrder)
import { swappedOrder } from './useQaTreeMoves'

export interface QaSiblingMove {
  canMoveUp: boolean
  canMoveDown: boolean
  busy: boolean
  onMoveUp: () => void
  onMoveDown: () => void
}

export function useQaSiblingReorder(controller: JobQaTabController) {
  const { tree, workspace } = controller
  const busy = tree.reorder.isPending

  function moveArea(areaId: string, direction: -1 | 1) {
    const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
    const areas = normalizeList<QaArea>(workspace.areasQuery.data)
    const siblings = levelsOf(floors, areas).find((level) => (
      level.areas.some((area) => area.id === areaId)
    ))?.areas ?? []
    const order = swappedOrder(siblings, siblings.findIndex((area) => area.id === areaId), direction)
    if (order.length === 0) return
    tree.reorder.mutate({ nodeType: 'area', order })
  }

  function moveChecklist(areaId: string, checklistId: string, direction: -1 | 1) {
    const execution = workspace.areaExecQuery.data?.id === areaId
      ? workspace.areaExecQuery.data
      : undefined
    const siblings = execution ? checklistGroups(execution as QaAreaExecution).map((group) => group.checklist) : []
    const order = swappedOrder(siblings, siblings.findIndex((row) => row.id === checklistId), direction)
    if (order.length === 0) return
    tree.reorder.mutate({ nodeType: 'checklist_instance', order })
  }

  function areaMove(areaId: string, siblingIds: readonly string[]): QaSiblingMove {
    const index = siblingIds.indexOf(areaId)
    return {
      canMoveUp: index > 0,
      canMoveDown: index >= 0 && index < siblingIds.length - 1,
      busy,
      onMoveUp: () => moveArea(areaId, -1),
      onMoveDown: () => moveArea(areaId, 1),
    }
  }

  function checklistMove(areaId: string, checklistId: string, siblingIds: readonly string[]): QaSiblingMove {
    const index = siblingIds.indexOf(checklistId)
    return {
      canMoveUp: index > 0,
      canMoveDown: index >= 0 && index < siblingIds.length - 1,
      busy,
      onMoveUp: () => moveChecklist(areaId, checklistId, -1),
      onMoveDown: () => moveChecklist(areaId, checklistId, 1),
    }
  }

  function forArea(areaId: string): QaSiblingMove {
    const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
    const areas = normalizeList<QaArea>(workspace.areasQuery.data)
    const siblings = levelsOf(floors, areas).find((level) => (
      level.areas.some((area) => area.id === areaId)
    ))?.areas ?? []
    return areaMove(areaId, siblings.map((area) => area.id))
  }

  function forChecklist(areaId: string, checklistId: string): QaSiblingMove {
    const execution = workspace.areaExecQuery.data?.id === areaId
      ? workspace.areaExecQuery.data
      : undefined
    const siblings = execution ? checklistGroups(execution as QaAreaExecution) : []
    return checklistMove(areaId, checklistId, siblings.map((group) => group.checklist.id))
  }

  return { areaMove, checklistMove, forArea, forChecklist }
}
